import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { lazyWithRetry } from './utils/lazyWithRetry';
import PrivateRoute, { TenantRoute, LandlordRoute } from './components/PrivateRoute';
import { apiGet } from './api';
import { buildPortalUrl } from './utils/portalLink';
import { websiteRedirectFor, websiteSubscriptionOnboardingPath } from './utils/authRedirect';
import ScrollToTop from './components/scroll-to-top';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import MaintenanceScreen from './components/MaintenanceScreen';
import KycAlertBanner from './components/KycAlertBanner';
import KycAlertModal from './components/KycAlertModal';
import PushManager from './components/PushManager';
import WhatsappCommunityChat from './components/WhatsappCommunityChat';
import TenantDashboardLayout from './components/TenantDashboardLayout';
import TenantLegacyRoute, { TenantDashboardEntry, TenantProfileEntry, TenantIndexRoute } from './components/tenant/TenantLegacyRoute';
import SubscriptionOnboardingGate from './components/SubscriptionOnboardingGate';
import { resolvePrimaryRole } from './utils/rolePrecedence';
const WebsiteWorkspace = lazyWithRetry(() => import('./workspace/WebsiteWorkspace'));

function ExternalWebsiteEntry({ path, children }: { path?: string; children?: React.ReactNode }) {
  const location = useLocation();
  let role: string | null = null;
  try { role = resolvePrimaryRole(JSON.parse(localStorage.getItem('srs_user') ?? 'null')); } catch { /* Invalid session is handled by PrivateRoute. */ }
  return role && ['landlord', 'agent', 'agency'].includes(role)
    ? <Navigate to={`/${role}${path ?? location.pathname}${location.search}${location.hash}`} replace />
    : <>{children ?? <Navigate to="/login" replace />}</>;
}

// Lazy load route components for code splitting
const Video = lazy(() => import('./pages/index/video'));
const Map = lazy(() => import('./pages/index/map'));
const ListLayoutOne = lazy(() => import('./pages/property/list-layout-one'));
const Grid = lazy(() => import('./pages/property/grid'));
const HalfMapTwo = lazy(() => import('./pages/property/half-map-2'));
// Canonical property detail page. Reached at /property/:id/:slug (slug is for
// SEO / social sharing only — the page always fetches by ID).
const PropertyDetail = lazy(() => import('./pages/property/single-property-1'));
const Agents = lazy(() => import('./pages/agents/agents'));
const AgentPage = lazy(() => import('./pages/agents/agent-page'));
const Agencies = lazy(() => import('./pages/agents/agencies'));
const AgencyPage = lazy(() => import('./pages/agents/agency-page'));
const Dashboard = lazy(() => import('./pages/admin/dashboard'));
const Payment = lazy(() => import('./pages/admin/payment'));
const MyProfile = lazy(() => import('./pages/admin/my-profile'));
const MyProperty = lazy(() => import('./pages/admin/my-property'));
const BookmarkList = lazy(() => import('./pages/admin/bookmark-list'));
const ChangePassword = lazy(() => import('./pages/admin/change-password'));
const CompareProperty = lazy(() => import('./pages/property/compare-property'));
// Legacy property-submission links now enter the role-specific website workspace.
// The workspace contains the complete listing wizard and its existing API workflow.
// Authentication continues to use the shared HttpOnly session cookie.
function SubmitPropertyRedirect() {
  return <ExternalWebsiteEntry path="/properties/new" />;
}
const Blog = lazy(() => import('./pages/blog/blog'));
const BlogDetail = lazy(() => import('./pages/blog/blog-detail'));
const Component = lazy(() => import('./pages/component'));
const Pricing = lazy(() => import('./pages/pricing'));
const Error = lazy(() => import('./pages/error'));
const Contact = lazy(() => import('./pages/contact'));
const AboutUs = lazy(() => import('./pages/about-us'));
const Checkout = lazy(() => import('./pages/subscription-checkout'));
const PaymentRecover = lazy(() => import('./pages/payments/recover'));
const CreateAccount = lazy(() => import('./pages/admin/create-account'));

function RegisterReferralRedirect() {
  const location = useLocation();
  return <Navigate to={`/create-account${location.search}`} replace />;
}
const ConfirmIdentity = lazy(() => import('./pages/admin/confirm-identity'));
const Referrals = lazy(() => import('./pages/account/referrals'));
const SignupCheckout = lazy(() => import('./pages/subscription/signup-checkout'));
const ChoosePlan = lazy(() => import('./pages/subscription/choose-plan'));
const MySubscription = lazy(() => import('./pages/account/my-subscription'));
const ReportVerify = lazy(() => import('./pages/reports/verify'));
const Login = lazy(() => import('./pages/admin/login'));
const Faq = lazy(() => import('./pages/faq'));
const HowItWorks = lazy(() => import('./pages/how-it-works'));
const PrivacyPolicy = lazy(() => import('./pages/privacy-policy'));
const TermsOfUse = lazy(() => import('./pages/terms-of-use'));
const TenantPropertyBrowse = lazyWithRetry(() => import('./pages/tenant/browse'));
const TenantVerification = lazyWithRetry(() => import('./pages/tenant/verification'));
const TenantDashboard = lazyWithRetry(() => import('./pages/tenant/dashboard'));
const TenantEnquiries = lazyWithRetry(() => import('./pages/tenant/enquiries'));
const TenantNotifications = lazyWithRetry(() => import('./pages/tenant/notifications'));
const TenantProfile = lazyWithRetry(() => import('./pages/tenant/profile'));
const TenantChangePassword = lazyWithRetry(() => import('./pages/tenant/change-password'));
const TenantConversations = lazyWithRetry(() => import('./pages/tenant/conversations'));
const TenantConversationDetail = lazyWithRetry(() => import('./pages/tenant/conversation-detail'));
const TenantSavedProperties = lazyWithRetry(() => import('./pages/tenant/saved-properties'));
const TenantSubscription = lazyWithRetry(() => import('./pages/tenant/subscription'));
const TenantReferrals = lazyWithRetry(() => import('./pages/tenant/referrals'));
const TenantPropertyRequests = lazyWithRetry(() => import('./pages/tenant/property-requests'));
const AddReview = lazy(() => import('./pages/add-review'));
const PropertyRequests = lazy(() => import('./pages/admin/property-requests'));
const SearchResults = lazy(() => import('./pages/search-results'));
const AgencyOnboarding = lazy(() => import('./pages/admin/agency-onboarding'));

// Loading fallback component
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--srs-bg)'
  }}>
    <div className="loading-spinner" style={{
      width: '50px',
      height: '50px',
      border: '4px solid color-mix(in srgb, var(--srs-primary) 10%, transparent)',
      borderTopColor: 'var(--srs-gold)',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }}></div>
  </div>
);

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [maintenance, setMaintenance] = useState<{ message?: string } | null>(null);

  // Show a full-page maintenance notice when api.ts reports maintenance mode
  // (503 + { maintenance: true }). Event-driven so there is no extra request on
  // a healthy load — the screen appears the moment any API call hits the gate.
  useEffect(() => {
    const onMaintenance = (e: Event) => {
      const detail = (e as CustomEvent<{ message?: string }>).detail;
      setMaintenance({ message: detail?.message });
    };
    window.addEventListener('srs:maintenance', onMaintenance);
    return () => window.removeEventListener('srs:maintenance', onMaintenance);
  }, []);

  // Redirect to /login when api.ts detects an expired JWT (401 with a stored token).
  // Using a custom event keeps api.ts free of React Router imports while still
  // respecting the BrowserRouter basename configured in index.tsx.
  useEffect(() => {
    const handleSessionExpired = () => navigate('/login', { replace: true, state: { from: location.pathname + location.search + location.hash } });
    window.addEventListener('srs:session-expired', handleSessionExpired);
    return () => window.removeEventListener('srs:session-expired', handleSessionExpired);
  }, [navigate, location.pathname, location.search, location.hash]);

  // Cross-tab sync: if the user logs out (or in) in another tab, mirror it here.
  // The Web Storage "storage" event fires in every tab EXCEPT the one that made the change.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'srs_user' && !e.newValue) {
        // Session cleared in another tab — redirect this tab to login
        navigate('/login');
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [navigate]);

  // Automatic application routing + session recovery (runs once per full load:
  // refresh, reopened browser, bookmarked URL, manual URL entry).
  //
  // Validate the HttpOnly-cookie session against the backend and let the backend
  // (auth_routing) decide whether this user belongs on the Website. Portal users
  // who land on the Website are sent straight to the Portal — they never see a
  // Website dashboard. Anonymous visitors get a 401 (no prior session) and stay
  // on the public site. No loop: the bounce target is the user's home app, which
  // is always one of their allowed apps.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Cookie-only sessions still set srs_user on login; skip remote /auth/me for anonymous visitors.
      if (!localStorage.getItem('srs_user')) {
        return;
      }
      try {
        const res = await apiGet<{ data?: unknown }>('/auth/me');
        if (cancelled) return;
        const me = (res && typeof res === 'object' && 'data' in res)
          ? (res as { data?: any }).data
          : (res as any);
        if (!me) return;
        // Session recovery — cache the freshly-validated user.
        localStorage.setItem('srs_user', JSON.stringify(me));
        window.dispatchEvent(new Event('userUpdated'));

        const pathname = window.location.pathname;
        const subscriptionPath = websiteSubscriptionOnboardingPath(me, pathname);
        if (subscriptionPath) {
          navigate(subscriptionPath, { replace: true });
          return;
        }

        const portalPath = websiteRedirectFor(me);
        if (portalPath) {
          window.location.href = buildPortalUrl(portalPath);
        }
      } catch {
        // Anonymous visitor or expired/invalid cookie — nothing to enforce here.
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (maintenance) {
    return <MaintenanceScreen message={maintenance.message} />;
  }

  return (
    <ErrorBoundary>
      <SiteSettingsProvider>
      <Suspense fallback={<LoadingFallback />}>
        {/* Global KYC nudge — shown on all pages for unverified tenants */}
        <SubscriptionOnboardingGate />
        <KycAlertBanner />
        <KycAlertModal />
        {/* Foreground push toasts (when the user has opted into notifications) */}
        <PushManager />
        <WhatsappCommunityChat />

        <Routes>
          {(['landlord', 'agent', 'agency'] as const).map(role => <Route key={role} path={`/${role}/*`} element={<WebsiteWorkspace key={role} role={role} />} />)}
          {/* ── Public routes ─────────────────────────────────────────────── */}
          <Route path='/' element={<Video />} />
          {/* Demo home templates → canonical home */}
          <Route path='/home-2' element={<Navigate to="/" replace />} />
          <Route path='/home-3' element={<Navigate to="/" replace />} />
          <Route path='/home-4' element={<Navigate to="/" replace />} />
          <Route path='/home-5' element={<Navigate to="/" replace />} />
          <Route path='/home-6' element={<Navigate to="/" replace />} />
          <Route path='/home-7' element={<Navigate to="/" replace />} />
          <Route path='/home-8' element={<Navigate to="/" replace />} />
          <Route path='/home-9' element={<Navigate to="/" replace />} />
          <Route path='/home-10' element={<Navigate to="/" replace />} />
          <Route path='/home-11' element={<Navigate to="/" replace />} />
          <Route path='/video' element={<Navigate to="/" replace />} />
          <Route path='/map' element={<Map />} />

          {/* Canonical listing paths; legacy layout demos redirect */}
          <Route path='/list-layout-one' element={<ListLayoutOne />} />
          <Route path='/list-layout-two' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/list-layout-three' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/list-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/list-layout-full' element={<Navigate to="/list-layout-one" replace />} />
          <Route path='/grid-layout-with-sidebar' element={<Navigate to="/grid" replace />} />
          <Route path='/classical-layout-with-sidebar' element={<Navigate to="/grid" replace />} />
          <Route path='/grid-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/grid' element={<Grid />} />
          <Route path='/classical-property' element={<Navigate to="/grid" replace />} />
          <Route path='/classical-layout-with-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/half-map' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/half-map-2' element={<HalfMapTwo />} />
          {/* Canonical property detail — SEO-friendly slug URL. Slug is optional
              (page redirects to the canonical slug once the property loads). */}
          <Route path='/property/:id' element={<PropertyDetail />} />
          <Route path='/property/:id/:slug' element={<PropertyDetail />} />
          <Route path='/agents' element={<Agents />} />
          <Route path='/agent-page' element={<AgentPage />} />
          <Route path='/agent-page/:id' element={<AgentPage />} />
          <Route path='/agencies' element={<Agencies />} />
          <Route path='/agency-page' element={<AgencyPage />} />
          <Route path='/agency-page/:id' element={<AgencyPage />} />
          <Route path='/compare-property' element={<CompareProperty />} />
          <Route path='/blog' element={<Blog />} />
          <Route path='/blog-detail' element={<BlogDetail />} />
          <Route path='/blog-detail/:id' element={<BlogDetail />} />
          <Route path='/component' element={<Component />} />
          <Route path='/pricing' element={<Pricing />} />
          <Route path='/404' element={<Error />} />
          <Route path='/contact' element={<Contact />} />
          <Route path='/about-us' element={<AboutUs />} />
          <Route path='/how-it-works' element={<HowItWorks />} />
          <Route path='/register' element={<RegisterReferralRedirect />} />
          <Route path='/create-account' element={<CreateAccount />} />
          <Route path='/confirm-identity' element={<ConfirmIdentity />} />
          <Route path='/subscription/signup-checkout' element={<PrivateRoute><SignupCheckout /></PrivateRoute>} />
          <Route path='/subscription/choose-plan' element={<PrivateRoute><ExternalWebsiteEntry path="/subscription/choose-plan"><ChoosePlan /></ExternalWebsiteEntry></PrivateRoute>} />
          <Route path='/account/my-subscription' element={<PrivateRoute><ExternalWebsiteEntry path="/account/my-subscription"><TenantLegacyRoute to="/tenant/subscription"><MySubscription /></TenantLegacyRoute></ExternalWebsiteEntry></PrivateRoute>} />
          <Route path='/account/referrals' element={<PrivateRoute><ExternalWebsiteEntry path="/account/referrals"><TenantLegacyRoute to="/tenant/referrals"><Referrals /></TenantLegacyRoute></ExternalWebsiteEntry></PrivateRoute>} />
          <Route path='/reports/verify/:qrToken' element={<ReportVerify />} />
          <Route path='/login' element={<Login />} />
          <Route path='/faq' element={<Faq />} />
          <Route path='/privacy-policy' element={<PrivacyPolicy />} />
          <Route path='/terms-of-use' element={<TermsOfUse />} />
          <Route path='/terms-and-conditions' element={<Navigate to="/terms-of-use" replace />} />
          <Route path='/place-search' element={<Navigate to="/half-map-2" replace />} />
          <Route path='/search-results' element={<SearchResults />} />

          {/* ── Private routes — redirect to /login if no session ────────── */}
          <Route element={<PrivateRoute />}>
            {/* Any authenticated user */}
            <Route path='/dashboard' element={<ExternalWebsiteEntry path="/dashboard"><TenantLegacyRoute to="/tenant/dashboard"><Dashboard /></TenantLegacyRoute></ExternalWebsiteEntry>} />
            <Route path='/payment' element={<ExternalWebsiteEntry path="/payments"><Payment /></ExternalWebsiteEntry>} />
            <Route path='/my-profile' element={<ExternalWebsiteEntry path="/profile"><TenantLegacyRoute to="/tenant/profile"><MyProfile /></TenantLegacyRoute></ExternalWebsiteEntry>} />
            <Route path='/bookmark-list' element={<TenantLegacyRoute to="/tenant/saved"><BookmarkList /></TenantLegacyRoute>} />
            <Route path='/change-password' element={<ExternalWebsiteEntry path="/change-password"><TenantLegacyRoute to="/tenant/change-password"><ChangePassword /></TenantLegacyRoute></ExternalWebsiteEntry>} />
            <Route path='/checkout' element={<ExternalWebsiteEntry path="/checkout"><Checkout /></ExternalWebsiteEntry>} />
            <Route path='/payments/recover/:token' element={<ExternalWebsiteEntry><PaymentRecover /></ExternalWebsiteEntry>} />
            <Route path='/add-review' element={<AddReview />} />

            {/* Landlord / Agent / Agency only */}
            <Route element={<LandlordRoute />}>
              {/* Agency onboarding — requires a non-tenant role */}
              <Route path='/agency-onboarding' element={<ExternalWebsiteEntry path="/agency-profile"><AgencyOnboarding /></ExternalWebsiteEntry>} />
              <Route path='/my-property' element={<ExternalWebsiteEntry path="/properties"><MyProperty /></ExternalWebsiteEntry>} />
              <Route path='/submit-property' element={<SubmitPropertyRedirect />} />
              <Route path='/submit-property-dashboard' element={<SubmitPropertyRedirect />} />
              <Route path='/property-requests' element={<ExternalWebsiteEntry path="/tenant-property-requests"><PropertyRequests /></ExternalWebsiteEntry>} />
            </Route>

            {/* Tenant only — dedicated dashboard shell (no public header/footer) */}
            <Route element={<TenantRoute />}>
              <Route path="/tenant" element={<TenantDashboardLayout />}>
                <Route index element={<TenantIndexRoute />} />
                <Route path="dashboard" element={<TenantDashboardEntry><TenantDashboard /></TenantDashboardEntry>} />
                <Route path="browse" element={<TenantPropertyBrowse />} />
                <Route path="enquiries" element={<TenantEnquiries />} />
                <Route path="enquiries/:id" element={<TenantEnquiries />} />
                <Route path="notifications" element={<TenantNotifications />} />
                <Route path="profile" element={<TenantProfileEntry><TenantProfile /></TenantProfileEntry>} />
                <Route path="verification" element={<TenantVerification />} />
                <Route path="change-password" element={<TenantChangePassword />} />
                <Route path="conversations" element={<TenantConversations />} />
                <Route path="conversations/:id" element={<TenantConversationDetail />} />
                <Route path="saved" element={<TenantSavedProperties />} />
                <Route path="subscription" element={<TenantSubscription />} />
                <Route path="referrals" element={<TenantReferrals />} />
                <Route path="property-requests" element={<TenantPropertyRequests />} />
                <Route path="*" element={<div className="srs-module"><h1>Page not found</h1><p>Choose a module from the navigation.</p></div>} />
              </Route>
            </Route>
          </Route>
        </Routes>
        <ScrollToTop />
      </Suspense>
      </SiteSettingsProvider>
    </ErrorBoundary>
  );
}

export default App;

